import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Select from 'react-select';
import Icon from '../icon/Icon.jsx';
import Tooltip from '../tooltip/Tooltip.jsx';
import FailedSavesMenu from './FailedSavesMenu.jsx';

import s from './TopBar.module.scss';

// VenueSelector Subcomponent ------------------------------------------------------------------------------------------
// Drop-down list of venues at the left of the TopBar
const VenueSelector = ({ venueId, venueBadgeUrl, venueSummaries, onVenueSelect }) => {
    const venueSelectionProps = () => ({
        clearable: false,
        options: venueSummaries.map(venue => ({ value: venue.id, label: venue.name })),
        value: venueId,
        onChange: option => onVenueSelect(option.value),
    });

    return (
        <div className={s.selectWrapper}>
            <div className={s.iconWrapper}>
                <Icon url={venueBadgeUrl} size={26} />
            </div>
            <Select {...venueSelectionProps()} />
        </div>
    );
};

VenueSelector.propTypes = {
    venueId: PropTypes.string.isRequired,
    venueBadgeUrl: PropTypes.string.isRequired,
    venueSummaries: PropTypes.arrayOf(PropTypes.object).isRequired,

    onVenueSelect: PropTypes.func.isRequired,
};

// ViewSelector Subcomponent -------------------------------------------------------------------------------------------
// Wrapper for the buttons in the middle of the TopBar
const ViewSelector = props => {
    const middleMenu = {
        list: 'POI LIST',
        map: 'MAP VIEW',
        publish: 'PUBLISH',
        // import : 'IMPORT/EXPORT',
    };

    const renderButton = buttonName => {
        const isSelected = selectedView === buttonName;
        const classes = classNames(s.menuItem, { [s.selected]: isSelected });

        return (
            <div key={buttonName} onClick={() => onViewSelect(buttonName)} className={classes} data-testid={`${buttonName}-button`}>
                {middleMenu[buttonName]}
            </div>
        );
    };

    const { buttonNames, selectedView, onViewSelect } = props;

    return <div className={s.middleMenu}>{buttonNames.map(renderButton)}</div>;
};

ViewSelector.propTypes = {
    buttonNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedView: PropTypes.string.isRequired,

    onViewSelect: PropTypes.func.isRequired,
};

// UpdatesInProgressPanel Subcomponent ---------------------------------------------------------------------------------
// Message box in the right of the TopBar that shows "UPDATES IN PROGRESS"
const UpdatesInProgressPanel = () => (
    <div className={s.saveIndicator}>
        <span className={s.indicatorLabel}>UPDATES IN PROGRESS</span>
    </div>
);

// UpdatesFailedPanel Subcomponent -------------------------------------------------------------------------------------
// Message box in the right of the TopBar that shows "UPDATES FAILED" -- along with child components
const UpdatesFailedPanel = props => {
    const className = classNames(s.errorIndicator, { [s.failuresShowing]: props.showFailureModal });

    return (
        <div className={className} onClick={props.onToggleFailureModal}>
            <span className={classNames('icon-warning', s.icon)} />
            <span className={s.indicatorLabel}>UPDATES FAILED</span>
            {props.children}
        </div>
    );
};

UpdatesFailedPanel.propTypes = {
    children: PropTypes.element.isRequired,
    showFailureModal: PropTypes.bool.isRequired,

    onToggleFailureModal: PropTypes.func.isRequired,
};

// TopBar Component ----------------------------------------------------------------------------------------------------
// Component showing VenueSelector, ViewSelector and various possibilities on the right
export default class TopBar extends Component {
    static propTypes = {
        isSaving: PropTypes.bool.isRequired,
        isV5: PropTypes.bool.isRequired,
        venueId: PropTypes.string.isRequired,
        selectedView: PropTypes.string.isRequired,
        venueBadgeUrl: PropTypes.string.isRequired,
        failedSaves: PropTypes.arrayOf(PropTypes.object).isRequired,
        venueSummaries: PropTypes.arrayOf(PropTypes.object).isRequired,

        onRetryFailures: PropTypes.func.isRequired,
        onSelectFailedPoi: PropTypes.func.isRequired,
        onVenueSelect: PropTypes.func.isRequired,
        onViewSelect: PropTypes.func.isRequired,
        onCancelAllUpdates: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);
        this.state = { showFailureModal: true };

        this.onSelectFailedPoi = this.onSelectFailedPoi.bind(this);
    }

    onSelectFailedPoi = id => {
        this.setState({ showFailureModal: false });
        this.props.onSelectFailedPoi(id);
    };

    render() {
        // -------------------------------------------------------------------------------------------------------------
        //
        // Functions to create props to send to child components as needed
        //
        // -------------------------------------------------------------------------------------------------------------
        const venueSelectorProps = () => ({
            venueId,
            venueBadgeUrl,
            venueSummaries,
            onVenueSelect,
        });

        const viewSelectorProps = () => ({
            // buttonNames: isV5 ? ['list', 'map', 'publish', 'import'] : ['list'],
            buttonNames: isV5 ? ['list', 'map', 'publish'] : ['list'],
            selectedView,
            onViewSelect,
        });

        const updatesFailedPanelProps = () => ({
            showFailureModal,
            onToggleFailureModal: () => this.setState({ showFailureModal: !showFailureModal }),
        });

        const failedSavesMenuProps = () => ({
            className: s.failedSaves,
            failedPois: failedSaves,
            onSelectPoi: this.onSelectFailedPoi,
            onCancel: onCancelAllUpdates,
            onRetry: onRetryFailures,
        });

        // RENDER setup

        const { showFailureModal } = this.state;
        const { onVenueSelect, venueSummaries, venueId, isV5, isSaving, failedSaves, onRetryFailures, venueBadgeUrl, selectedView, onViewSelect, onCancelAllUpdates } = this.props;

        // conditions defining which variant to show in the right of the TopBar
        const determineVariantToShow = () => {
            if (isSaving) return 'SHOW_SAVING';
            if (failedSaves.length > 0 && showFailureModal) return 'SHOW_FAILURE_WITH_MODAL';
            if (failedSaves.length > 0) return 'SHOW_FAILURE_WITH_TOOLTIP';
        };

        const variant = determineVariantToShow();

        // RENDER
        return (
            <div className={s.topBar}>
                <VenueSelector {...venueSelectorProps()} />
                <ViewSelector {...viewSelectorProps()} />

                <div className={s.logoWrapper}>
                    {variant === 'SHOW_SAVING' && <UpdatesInProgressPanel />}
                    {variant === 'SHOW_FAILURE_WITH_MODAL' && (
                        <UpdatesFailedPanel {...updatesFailedPanelProps()}>
                            <FailedSavesMenu {...failedSavesMenuProps()} />
                        </UpdatesFailedPanel>
                    )}
                    {variant === 'SHOW_FAILURE_WITH_TOOLTIP' && (
                        <UpdatesFailedPanel {...updatesFailedPanelProps()}>
                            <Tooltip arrowPosition="top" text="Show failures" />
                        </UpdatesFailedPanel>
                    )}

                    <div className={s.locusLogo} />
                </div>
            </div>
        );
    }
}
